import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useSupplierCreateEditDispatch, useSupplierCreateEditState } from './supplier-create-edit-context';
import SupplierAppService from '../../../../application/platform/sge/supplier/supplier-app-service';
import LocationAppService from '../../../../application/platform/base/location-app-service';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { ICountryResponse, IStateResponse, ICityResponse } from '../../../../model/base/location-response';

export const useSupplierCreateEditController = () => {
    const navigate = useNavigate();
    const dispatch = useSupplierCreateEditDispatch();
    const state = useSupplierCreateEditState();
    const { showMessage, showLoading } = useBaseLayout();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingZipCode, setIsLoadingZipCode] = useState(false);
    const [countries, setCountries] = useState<ICountryResponse[]>([]);
    const [states, setStates] = useState<IStateResponse[]>([]);
    const [cities, setCities] = useState<ICityResponse[]>([]);

    useEffect(() => {
        const countryId = state.supplierRequest.address?.countryId;
        if (countryId) {
            LocationAppService.listStates(countryId).then(setStates).catch(() => setStates([]));
        } else {
            setStates([]);
        }
    }, [state.supplierRequest.address?.countryId]);

    useEffect(() => {
        const stateId = state.supplierRequest.address?.stateId;
        if (stateId) {
            LocationAppService.listCities(stateId).then(setCities).catch(() => setCities([]));
        } else {
            setCities([]);
        }
    }, [state.supplierRequest.address?.stateId]);

    useEffect(() => {
        async function loadInitialData() {
            showLoading({ open: true });
            try {
                const countryData = await LocationAppService.listCountries();
                setCountries(countryData);

                if (state.supplierId) {
                    const data = await SupplierAppService.getById(Number(state.supplierId));
                    dispatch({
                        type: ACTION_TYPES.setSupplierRequest,
                        payload: {
                            name: data.personName,
                            supplierPersonType: data.supplierPersonType,
                            physicalPerson: data.cpf ? { cpf: data.cpf } : undefined,
                            legalPerson: data.cnpj ? { cnpj: data.cnpj } : undefined,
                            address: {
                                countryId: data.countryId,
                                stateId: data.stateId,
                                municipalityId: data.municipalityId,
                                street: data.addressStreet,
                                number: data.addressNumber,
                                neighborhood: data.addressNeighborhood,
                                zipCode: data.addressZipCode,
                                complement: data.addressComplement,
                            },
                            contact: {
                                email: data.contactEmail,
                                phone: data.contactPhone,
                                mobile: data.contactMobile,
                            }
                        }
                    });
                }
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível carregar os dados do fornecedor', detail: error?.details || error?.message, type: 'error' });
            } finally {
                showLoading({ open: false });
            }
        }
        loadInitialData();
    }, [state.supplierId]);

    function handleOnChange(e: any) {
        dispatch({ type: ACTION_TYPES.updateSupplierRequest, payload: { [e.target.name]: e.target.value } });
    }

    function handlePersonTypeChange(personType: string) {
        dispatch({ type: ACTION_TYPES.updateSupplierRequest, payload: { supplierPersonType: personType } });
    }

    function handleAddressChange(e: any) {
        dispatch({ type: ACTION_TYPES.updateAddress, payload: { [e.target.name]: e.target.value } });
    }

    function handleCountryChange(e: any) {
        dispatch({ type: ACTION_TYPES.updateAddress, payload: { countryId: e.target.value || undefined, stateId: undefined, municipalityId: undefined } });
    }

    function handleStateChange(e: any) {
        dispatch({ type: ACTION_TYPES.updateAddress, payload: { stateId: e.target.value || undefined, municipalityId: undefined } });
    }

    async function handleZipCodeBlur(e: any) {
        const raw = (e.target.value as string).replace(/\D/g, '');
        if (raw.length !== 8) return;
        setIsLoadingZipCode(true);
        try {
            const data = await LocationAppService.lookupZipCode(raw);
            if (data.countryId || data.stateId || data.cityId) {
                dispatch({
                    type: ACTION_TYPES.updateAddress,
                    payload: {
                        countryId: data.countryId ?? undefined,
                        stateId: data.stateId ?? undefined,
                        municipalityId: data.cityId ?? undefined,
                        street: data.street ?? undefined,
                        neighborhood: data.neighborhood ?? undefined,
                        complement: data.complement ?? undefined,
                    }
                });
            }
        } catch {
            // CEP não encontrado — usuário preenche manualmente
        } finally {
            setIsLoadingZipCode(false);
        }
    }

    function handleContactChange(e: any) {
        dispatch({ type: ACTION_TYPES.updateContact, payload: { [e.target.name]: e.target.value } });
    }

    function handlePhysicalPersonChange(e: any) {
        dispatch({ type: ACTION_TYPES.updatePhysicalPerson, payload: { [e.target.name]: e.target.value } });
    }

    function handleLegalPersonChange(e: any) {
        dispatch({ type: ACTION_TYPES.updateLegalPerson, payload: { [e.target.name]: e.target.value } });
    }

    async function handleSave() {
        const { name, supplierPersonType, physicalPerson, legalPerson, address } = state.supplierRequest;
        const isPF = (supplierPersonType || 'PF') === 'PF';
        const isValid = !!name
            && !!address?.zipCode
            && !!address?.countryId
            && !!address?.stateId
            && !!address?.municipalityId
            && !!address?.street
            && !!address?.number
            && !!address?.neighborhood
            && (isPF ? !!physicalPerson?.cpf : !!legalPerson?.cnpj);

        if (!isValid) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            return;
        }
        setIsSaving(true);
        try {
            if (state.supplierId) {
                await SupplierAppService.edit(Number(state.supplierId), state.supplierRequest);
                showMessage({ open: true, message: 'Fornecedor atualizado com sucesso!', type: 'success' });
            } else {
                await SupplierAppService.add(state.supplierRequest);
                showMessage({ open: true, message: 'Fornecedor adicionado com sucesso!', type: 'success' });
            }
            setTimeout(() => navigate('/fornecedores'), 1500);
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar o fornecedor', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function handleCancel() { navigate('/fornecedores'); }

    return {
        handleOnChange, handlePersonTypeChange,
        handleAddressChange, handleCountryChange, handleStateChange, handleZipCodeBlur,
        handleContactChange, handlePhysicalPersonChange, handleLegalPersonChange,
        handleSave, handleCancel,
        isSaving, isLoadingZipCode, countries, states, cities
    };
};
